/**
 * F-404-FE-03: NoteDecisionPage
 *
 * Post-session decision screen displayed after note generation.
 * - Progress indicator while note is being generated
 * - Listens for WebSocket NOTE_READY / NOTE_FAILED messages
 * - On success: "Edit Note Now" and "Save as Draft" with equal visual weight
 * - On failure: error message with option to view raw transcript
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, ApiError } from '@/lib/apiClient';
import type { Note, NoteStatus } from './types';

type PageState =
  | { phase: 'generating' }
  | { phase: 'ready'; note: Note }
  | { phase: 'failed'; message: string; sessionId?: string };

/** Derive decision page state from a note's current status */
function statusToPhase(status: NoteStatus): 'generating' | 'ready' | 'failed' {
  if (status === 'GENERATING') return 'generating';
  if (status === 'GENERATION_FAILED') return 'failed';
  return 'ready';
}

// ---- Generating Phase -------------------------------------------------------

function GeneratingView() {
  const [dotCount, setDotCount] = useState(1);

  useEffect(() => {
    const t = setInterval(() => setDotCount((d) => (d % 3) + 1), 600);
    return () => clearInterval(t);
  }, []);

  const dots = '.'.repeat(dotCount);

  return (
    <div className="flex flex-col items-center gap-6 py-12">
      {/* Animated medical cross / pulse */}
      <div className="relative flex items-center justify-center w-20 h-20">
        <div className="absolute inset-0 rounded-full bg-clinical-teal/10 animate-ping" />
        <div className="relative z-10 w-16 h-16 rounded-full bg-clinical-cream border-2 border-clinical-teal/30 flex items-center justify-center">
          <PulseIcon />
        </div>
      </div>

      <div className="text-center space-y-2">
        <h2 className="font-heading text-2xl font-semibold text-clinical-charcoal">
          Generating note{dots}
        </h2>
        <p className="text-base font-body text-clinical-text-muted max-w-xs">
          Your clinical note is being prepared from the session transcript.
          This usually takes 20–40 seconds.
        </p>
      </div>

      {/* Progress bar (indeterminate) */}
      <div className="w-64 h-1.5 bg-clinical-cream-dark rounded-full overflow-hidden">
        <div className="h-full bg-clinical-teal rounded-full animate-progressSlide" />
      </div>
    </div>
  );
}

// ---- Ready Phase ------------------------------------------------------------

interface ReadyViewProps {
  note: Note;
  onEditNow: () => void;
  onSaveAsDraft: () => void;
}

function ReadyView({ note, onEditNow, onSaveAsDraft }: ReadyViewProps) {
  return (
    <div className="flex flex-col items-center gap-8 py-8">
      {/* Success icon */}
      <div className="w-16 h-16 rounded-full bg-green-50 border-2 border-safety-green/30 flex items-center justify-center">
        <CheckCircleIcon />
      </div>

      <div className="text-center space-y-2">
        <h2 className="font-heading text-2xl font-semibold text-clinical-charcoal">
          Note ready
        </h2>
        <p className="text-base font-body text-clinical-text-muted">
          Your clinical note for{' '}
          <span className="font-semibold text-clinical-charcoal-soft">
            {note.templateName}
          </span>{' '}
          has been generated.
        </p>
      </div>

      {/* Equal-weight decision cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full max-w-lg">
        {/* Edit Note Now — left card */}
        <button
          type="button"
          onClick={onEditNow}
          className="group flex flex-col items-center gap-4 p-6 rounded-clinical border border-clinical-border bg-white shadow-clinical hover:shadow-clinical-md hover:border-clinical-teal/40 transition-all duration-200 min-h-[160px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-clinical-amber focus-visible:ring-offset-2"
        >
          <div className="w-12 h-12 rounded-full bg-clinical-teal/10 border border-clinical-teal/20 flex items-center justify-center group-hover:bg-clinical-teal/20 transition-colors">
            <PencilIcon className="text-clinical-teal" />
          </div>
          <div className="text-center">
            <p className="font-heading text-base font-semibold text-clinical-charcoal">
              Edit Note Now
            </p>
            <p className="text-sm font-body text-clinical-text-muted mt-1">
              Review and refine before completing
            </p>
          </div>
        </button>

        {/* Save as Draft — right card, equal visual weight */}
        <button
          type="button"
          onClick={onSaveAsDraft}
          className="group flex flex-col items-center gap-4 p-6 rounded-clinical border border-clinical-border bg-white shadow-clinical hover:shadow-clinical-md hover:border-clinical-amber/40 transition-all duration-200 min-h-[160px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-clinical-amber focus-visible:ring-offset-2"
        >
          <div className="w-12 h-12 rounded-full bg-clinical-amber/10 border border-clinical-amber/20 flex items-center justify-center group-hover:bg-clinical-amber/20 transition-colors">
            <BookmarkIcon className="text-clinical-amber" />
          </div>
          <div className="text-center">
            <p className="font-heading text-base font-semibold text-clinical-charcoal">
              Save as Draft
            </p>
            <p className="text-sm font-body text-clinical-text-muted mt-1">
              Finish later — it will wait for you
            </p>
          </div>
        </button>
      </div>

      <p className="text-xs font-body text-clinical-text-muted text-center max-w-xs">
        Both options save your note securely. You can always return to edit or
        complete it later from your pending notes.
      </p>
    </div>
  );
}

// ---- Failed Phase -----------------------------------------------------------

interface FailedViewProps {
  message: string;
  sessionId?: string;
}

function FailedView({ message, sessionId }: FailedViewProps) {
  const navigate = useNavigate();

  return (
    <div className="flex flex-col items-center gap-6 py-8">
      <div className="w-16 h-16 rounded-full bg-red-50 border-2 border-safety-red/30 flex items-center justify-center">
        <ExclamationIcon />
      </div>

      <div className="text-center space-y-2">
        <h2 className="font-heading text-2xl font-semibold text-clinical-charcoal">
          Note generation failed
        </h2>
        <p className="text-base font-body text-clinical-text-muted max-w-sm">
          {message ||
            'An error occurred while generating your note. The session transcript is still available.'}
        </p>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 w-full max-w-xs">
        <button
          type="button"
          className="btn-secondary flex-1"
          onClick={() => navigate('/notes/pending')}
        >
          View pending notes
        </button>
        {sessionId && (
          <button
            type="button"
            className="btn-primary flex-1"
            onClick={() => navigate(`/sessions/${sessionId}/transcript`)}
          >
            View transcript
          </button>
        )}
      </div>
    </div>
  );
}

// ---- Main Component --------------------------------------------------------

export function NoteDecisionPage() {
  const { noteId } = useParams<{ noteId: string }>();
  const navigate = useNavigate();

  const [pageState, setPageState] = useState<PageState>({ phase: 'generating' });
  const wsRef = useRef<WebSocket | null>(null);
  const noteRef = useRef<Note | null>(null);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Fetch current note status on mount
  useEffect(() => {
    if (!noteId) return;
    let cancelled = false;

    async function loadNote() {
      try {
        const { data: note } = await api.get<{ data: Note }>(`/notes/${noteId}`);
        if (cancelled) return;
        noteRef.current = note;

        const phase = statusToPhase(note.status);
        if (phase === 'generating') {
          setPageState({ phase: 'generating' });
          // Start WebSocket listener (handled in next effect)
        } else if (phase === 'ready') {
          setPageState({ phase: 'ready', note });
        } else {
          setPageState({
            phase: 'failed',
            message: 'Note generation failed. Please check your pending notes.',
            sessionId: note.sessionId,
          });
        }
      } catch (err) {
        if (!cancelled) {
          const message =
            err instanceof ApiError ? err.message : 'Failed to load note.';
          setPageState({ phase: 'failed', message });
        }
      }
    }

    void loadNote();
    return () => { cancelled = true; };
  }, [noteId]);

  // WebSocket listener for NOTE_READY / NOTE_FAILED
  useEffect(() => {
    if (pageState.phase !== 'generating' || !noteId) return;

    // Connect to WebSocket (re-use token from localStorage)
    const token = localStorage.getItem('ci_auth_token');
    const wsUrl = `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.host}/ws?token=${token ?? ''}`;

    let ws: WebSocket;
    try {
      ws = new WebSocket(wsUrl);
    } catch {
      // If WS fails, fall back to polling
      startPolling();
      return;
    }

    wsRef.current = ws;

    ws.addEventListener('message', (event: MessageEvent<string>) => {
      try {
        const msg = JSON.parse(event.data) as {
          type: string;
          noteId?: string;
          note?: Note;
          error?: string;
          sessionId?: string;
        };

        if (msg.noteId && msg.noteId !== noteId) return;

        if (msg.type === 'NOTE_READY' && msg.note) {
          setPageState({ phase: 'ready', note: msg.note });
          ws.close();
        } else if (msg.type === 'NOTE_FAILED') {
          setPageState({
            phase: 'failed',
            message: msg.error ?? 'Note generation failed.',
            sessionId: msg.sessionId,
          });
          ws.close();
        }
      } catch {
        // Ignore non-JSON messages (e.g. binary audio frames)
      }
    });

    ws.addEventListener('error', () => {
      // Fall back to polling if WebSocket connection fails
      ws.close();
      startPolling();
    });

    ws.addEventListener('close', () => {
      wsRef.current = null;
    });

    return () => {
      ws.close();
      wsRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageState.phase, noteId]);

  // Cleanup polling interval on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current !== null) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, []);

  // Polling fallback: check note status every 5 seconds (with 60s timeout)
  const NOTE_POLL_TIMEOUT_MS = 60_000;
  const startPolling = useCallback(() => {
    if (!noteId) return;
    // Clear any existing poll interval before starting a new one
    if (pollIntervalRef.current !== null) {
      clearInterval(pollIntervalRef.current);
    }
    const pollStartTime = Date.now();
    pollIntervalRef.current = setInterval(async () => {
      // Timeout: if polling has been running for more than 60 seconds, give up
      if (Date.now() - pollStartTime > NOTE_POLL_TIMEOUT_MS) {
        if (pollIntervalRef.current !== null) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
        setPageState({
          phase: 'failed',
          message: 'Note generation is taking longer than expected. Please try refreshing.',
          sessionId: noteRef.current?.sessionId,
        });
        return;
      }

      try {
        const { data: note } = await api.get<{ data: Note }>(`/notes/${noteId}`);
        const phase = statusToPhase(note.status);
        if (phase !== 'generating') {
          if (pollIntervalRef.current !== null) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
          if (phase === 'ready') {
            setPageState({ phase: 'ready', note });
          } else {
            setPageState({
              phase: 'failed',
              message: 'Note generation failed.',
              sessionId: note.sessionId,
            });
          }
        }
      } catch {
        if (pollIntervalRef.current !== null) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
      }
    }, 5_000);
  }, [noteId]);

  const handleEditNow = useCallback(() => {
    if (noteId) navigate(`/notes/${noteId}/edit`);
  }, [noteId, navigate]);

  const handleSaveAsDraft = useCallback(() => {
    navigate('/notes/pending');
  }, [navigate]);

  return (
    <div
      className="min-h-screen flex items-start justify-center pt-8 px-4 pb-12"
      style={{ backgroundColor: '#FAFAF7' }}
    >
      <div className="w-full max-w-2xl">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="font-heading text-3xl font-semibold text-clinical-charcoal">
            Clinical Note
          </h1>
          <p className="text-base font-body text-clinical-text-muted mt-2">
            Session complete
          </p>
        </div>

        {/* Card */}
        <div className="clinical-card p-8">
          {pageState.phase === 'generating' && <GeneratingView />}
          {pageState.phase === 'ready' && (
            <ReadyView
              note={pageState.note}
              onEditNow={handleEditNow}
              onSaveAsDraft={handleSaveAsDraft}
            />
          )}
          {pageState.phase === 'failed' && (
            <FailedView
              message={pageState.message}
              sessionId={pageState.sessionId}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// --- Inline icons ---

function PulseIcon() {
  return (
    <svg
      className="w-8 h-8 text-clinical-teal"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
    </svg>
  );
}

function CheckCircleIcon() {
  return (
    <svg
      className="w-8 h-8 text-safety-green"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4" />
      <circle cx="12" cy="12" r="10" />
    </svg>
  );
}

function ExclamationIcon() {
  return (
    <svg
      className="w-8 h-8 text-safety-red"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
      />
    </svg>
  );
}

interface IconProps {
  className?: string;
}

function PencilIcon({ className }: IconProps) {
  return (
    <svg
      className={`w-6 h-6 ${className ?? ''}`}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
      />
    </svg>
  );
}

function BookmarkIcon({ className }: IconProps) {
  return (
    <svg
      className={`w-6 h-6 ${className ?? ''}`}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"
      />
    </svg>
  );
}
